import { useState } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { ChevronRight, Plus, Trash2, X } from "lucide-react";
import { PanelSection } from "../PanelSection";
import type { CableTypeSummaryProps } from "./types";

const OPEN_STORAGE_KEY = "cables.cableTypes.open";

// Collapsed unless he opened it: it shares the column with the runs, and his choice outlives a reload.
const readIsOpen = (): boolean => {
  try {
    return window.localStorage.getItem(OPEN_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
};

const writeIsOpen = (isOpen: boolean): void => {
  try {
    window.localStorage.setItem(OPEN_STORAGE_KEY, isOpen ? "1" : "0");
  } catch {
    // A blocked store only costs remembering the choice.
  }
};

export const CableTypeSummary = ({
  entries,
  selectedKey,
  onSelect,
  formatMetres,
  boardName,
  onCreate,
  createdTypes = [],
  onRemove,
}: CableTypeSummaryProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(readIsOpen);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [draft, setDraft] = useState<string | null>(null);

  const startCreate = () => {
    setDraft("");
    setIsOpen(true);
    writeIsOpen(true);
  };

  const create = () => {
    const name = (draft ?? "").trim();
    if (!name) return;
    onCreate?.(name);
    setDraft(null);
  };

  const longest = Math.max(...entries.map((entry) => entry.metres), 1);

  if (!entries.length) return null;

  return (
    <PanelSection
      tone="neutral"
      title={t("smartSurveyor.cablesBeta.cableTypesTitle")}
      count={entries.length}
      isOpen={isOpen}
      onToggle={() =>
        setIsOpen((open) => {
          writeIsOpen(!open);
          return !open;
        })
      }
      testId="cable-types"
      action={
        selectedKey === null && onCreate ? (
          <button
            type="button"
            onClick={startCreate}
            aria-label={t("smartSurveyor.cablesBeta.cableTypesAdd")}
            title={t("smartSurveyor.cablesBeta.cableTypesAdd")}
            className="grid h-8 w-8 place-items-center rounded-md text-slate-600 hover:bg-blue-50 hover:text-blue-700"
          >
            <Plus size={16} />
          </button>
        ) : selectedKey !== null ? (
          <button
            type="button"
            onClick={() => onSelect(null)}
            className="flex items-center gap-1 rounded-md px-2 py-1 text-[13px] font-medium text-blue-700 hover:bg-blue-50"
          >
            <X size={13} />
            {t("smartSurveyor.cablesBeta.cableTypesClear")}
          </button>
        ) : undefined
      }
    >
      {draft !== null && (
        <div data-testid="cable-type-create" className="mb-1.5 flex items-center gap-1.5">
          <input
            autoFocus
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") create();
              if (event.key === "Escape") setDraft(null);
            }}
            placeholder={t("smartSurveyor.cablesBeta.cableTypesAddPlaceholder")}
            aria-label={t("smartSurveyor.cablesBeta.cableTypesAdd")}
            className="min-w-0 flex-1 rounded-md border border-slate-300 px-2.5 py-1.5 text-[14px] text-slate-800 outline-none focus:border-blue-500"
          />
          <button
            type="button"
            disabled={!draft.trim()}
            onClick={create}
            className="rounded-md bg-blue-600 px-3 py-1.5 text-[13px] font-semibold text-white hover:opacity-90 disabled:opacity-40"
          >
            {t("smartSurveyor.cablesBeta.cableTypesAddSave")}
          </button>
          <button
            type="button"
            onClick={() => setDraft(null)}
            className="rounded-md px-2 py-1.5 text-[13px] text-slate-600 hover:bg-slate-100"
          >
            {t("smartSurveyor.cablesBeta.cancel")}
          </button>
        </div>
      )}
      <ul className="flex flex-col gap-0.5">
        {entries.map((entry) => {
          const isSelected = selectedKey === entry.key;
          const isExpanded = expandedKey === entry.key;
          const isUntyped = !entry.key;
          const isUnused = entry.circuitIds.length === 0;
          const isRemovable = isUnused && Boolean(onRemove) && createdTypes.includes(entry.label);
          return (
            <li key={entry.key || "untyped"}>
              <div
                className={clsx(
                  "flex items-center gap-1 rounded-md",
                  isSelected
                    ? "bg-blue-50 ring-1 ring-blue-400"
                    : "hover:bg-slate-50",
                )}
              >
                <button
                  type="button"
                  aria-expanded={isExpanded}
                  aria-label={t("smartSurveyor.cablesBeta.cableTypesByBoard")}
                  onClick={() => setExpandedKey(isExpanded ? null : entry.key)}
                  className="grid h-8 w-7 flex-none place-items-center text-slate-500 hover:text-slate-800"
                >
                  <ChevronRight
                    size={14}
                    className={clsx(
                      "transition-transform",
                      isExpanded && "rotate-90",
                    )}
                  />
                </button>
                <button
                  type="button"
                  data-testid="cable-type-row"
                  aria-pressed={isSelected}
                  onClick={() => onSelect(isSelected ? null : entry.key)}
                  className="flex min-w-0 flex-1 flex-col gap-1 py-1.5 pr-2 text-left"
                >
                  <span className="flex w-full items-baseline gap-2">
                    <span
                      className={clsx(
                        "min-w-0 flex-1 truncate text-[14px] font-medium",
                        isUntyped ? "text-amber-700" : "text-slate-800",
                      )}
                    >
                      {isUntyped
                        ? t("smartSurveyor.cablesBeta.cableTypesUntyped")
                        : entry.label}
                    </span>
                    {isUnused ? (
                      <span className="flex-none text-[13px] text-slate-500">
                        {t("smartSurveyor.cablesBeta.cableTypesUnused")}
                      </span>
                    ) : (
                      <span className="flex-none text-[14px] font-semibold tabular-nums text-slate-900">
                        {formatMetres(entry.metres)} m
                      </span>
                    )}
                  </span>
                  <span className="block h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                    <span
                      className={clsx(
                        "block h-full rounded-full",
                        isUntyped ? "bg-amber-400" : "bg-emerald-500",
                      )}
                      style={{ width: `${(entry.metres / longest) * 100}%` }}
                    />
                  </span>
                </button>
                {isRemovable && (
                  <button
                    type="button"
                    onClick={() => onRemove?.(entry.label)}
                    aria-label={t("smartSurveyor.cablesBeta.cableTypesRemove")}
                    title={t("smartSurveyor.cablesBeta.cableTypesRemove")}
                    className="grid h-8 w-8 flex-none place-items-center rounded-md text-slate-500 hover:bg-red-50 hover:text-red-600"
                  >
                    <Trash2 size={15} />
                  </button>
                )}
              </div>
              {isExpanded && (
                <ul
                  data-testid="cable-type-boards"
                  className="mb-1 ml-7 mt-0.5 flex flex-col gap-0.5 border-l border-slate-200 pl-2"
                >
                  {entry.byBoard.map((share) => (
                    <li
                      key={share.board ?? "unfed"}
                      className="flex items-baseline gap-2 text-[13px] text-slate-600"
                    >
                      <span className="min-w-0 flex-1 truncate">
                        {boardName(share.board)}
                      </span>
                      <span className="flex-none tabular-nums">
                        {formatMetres(share.metres)} m
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </PanelSection>
  );
};
